// MultiStepFlow.tsx
import React, { useState, useRef } from 'react';
import { Modal, Button, Form, ProgressBar } from 'react-bootstrap';
import { patchRecordedWebmDuration } from '../../utils/patchRecordedWebmDuration';
import {
  createPromptMediaRecorder,
  getRecordingPermissionErrorMessage,
  requestCameraAndMicrophone,
} from '../../utils/mediaCapture';
import { toast } from 'react-toastify';

const MultiStepFlow: React.FC = () => {
  const [step, setStep] = useState(1);
  const [show, setShow] = useState(true);

  // State per step
  const [selectedPrompts, setSelectedPrompts] = useState<string[]>([]);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [description, setDescription] = useState('');

  // Video recording refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [category, setCategory] = useState<'general' | 'role' | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordedChunks = useRef<BlobPart[]>([]);
  const recordStartedAtRef = useRef<number | null>(null);
  const recordingTimeRef = useRef(0);
  const [stopped, setStopped] = useState(false);

  // Example prompts
  const generalPrompts = [
    'Can you briefly describe your professional background and experience relevant to your role in your field?',
    'Can you tell me a little about yourself?',
    'What type of roles interest you and why?',
    'What are your key strengths that make you a good fit for the positions you are seeking?',
    'Can you share an example of a challenging work situation you faced and how you handled it?',
  ];

  const roleSpecificPrompts = [
    'Tell me about a time you applied your specialized skills to solve a problem.',
    'How do you stay up to date with the latest tools and trends in your field?',
    'Walk me through your approach to a complex role-specific scenario.',
  ];

  /** Step 1: handle prompt selection */
  const handlePromptChange = (prompt: string) => {
    setSelectedPrompts((prev) =>
      prev.includes(prompt) ? prev.filter((p) => p !== prompt) : [...prev, prompt]
    );
  };

  const saveStep1 = async () => {
    await fetch('/api/save-prompts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompts: selectedPrompts }),
    });
    setStep(2);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const startRecording = async () => {
    try {
      const stream = await requestCameraAndMicrophone();

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      recordedChunks.current = [];

      const mediaRecorder = createPromptMediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const durationMs = Math.max(recordingTimeRef.current * 1000, 0);
        recordStartedAtRef.current = null;

        const blob = new Blob(recordedChunks.current, { type: 'video/webm' });
        clearInterval(timerRef.current!);

        void (async () => {
          const fixedBlob = await patchRecordedWebmDuration(blob, durationMs);
          setVideoBlob(fixedBlob);

          if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.src = URL.createObjectURL(fixedBlob);
          }
        })();
      };

      recordStartedAtRef.current = Date.now();
      mediaRecorder.start(1000);
      setRecording(true);
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const next = prev + 1;
          recordingTimeRef.current = next;
          return next;
        });
      }, 1000);
    } catch (error) {
      toast.error(getRecordingPermissionErrorMessage(error));
    }
  };

  // Stop recording
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      if (recorder.state === 'recording') {
        recorder.requestData();
      }
      recorder.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    setRecording(false);
    setStopped(true);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current!); // stop timer while paused
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const next = prev + 1;
          recordingTimeRef.current = next;
          return next;
        });
      }, 1000);
    }
  };

  const restartRecording = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
    }
    recordStartedAtRef.current = null;
    recordingTimeRef.current = 0;
    setVideoBlob(null);
    setRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    startRecording();
  };

  const saveStep2 = async () => {
    if (videoBlob) {
      const formData = new FormData();
      formData.append('video', videoBlob, 'recording.webm');

      await fetch('/api/upload-video', {
        method: 'POST',
        body: formData,
      });
    }
    setStep(3);
  };

  /** Step 3: save description */
  const saveStep3 = async () => {
    await fetch('/api/save-description', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    });
    setShow(false); // finish flow
  };

  return (
    <Modal show={show} fullscreen>
      <Modal.Header>
        <Modal.Title>Step {step} of 3</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 m-0" style={{ height: '100vh', overflow: 'hidden' }}>
        <ProgressBar now={(step / 3) * 100} variant="success" />

        {/* Step 1: Prompts */}
        {step === 1 && (
          <div className="p-3">
            <h5>Select Prompts</h5>

            {!category && (
              <div className="d-flex justify-content-around my-3">
                <Button variant="success" onClick={() => setCategory('general')}>
                  General Prompts
                </Button>
                <Button variant="success" onClick={() => setCategory('role')}>
                  Role-Specific Prompts
                </Button>
              </div>
            )}

            {category === 'general' &&
              generalPrompts.map((p) => (
                <Form.Check
                  key={p}
                  type="checkbox"
                  label={p}
                  checked={selectedPrompts.includes(p)}
                  onChange={() => handlePromptChange(p)}
                />
              ))}

            {category === 'role' &&
              roleSpecificPrompts.map((p) => (
                <Form.Check
                  key={p}
                  type="checkbox"
                  label={p}
                  checked={selectedPrompts.includes(p)}
                  onChange={() => handlePromptChange(p)}
                />
              ))}
          </div>
        )}

        {/* Step 2: Video recording */}
        {step === 2 && (
          <div className="position-relative w-100 h-100 bg-black">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              controls={!!videoBlob && !recording}
              className="w-100 h-100 object-fit-cover"
              style={{ backgroundColor: 'black' }}
            />

            {(recording || isPaused) && (
              <div
                className="position-absolute bottom-0 start-50 translate-middle-x text-white mb-5"
                style={{ fontSize: '1.5rem', fontFamily: 'monospace' }}
              >
                {formatTime(recordingTime)}
              </div>
            )}

            {/* Start button */}
            {!recording && !videoBlob && (
              <div className="position-absolute bottom-0 end-0 mb-4 me-4">
                <Button
                  onClick={startRecording}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'green',
                    border: 'none',
                  }}
                />
              </div>
            )}

            {/* Pause / Resume / Stop / Play controls */}
            {(recording || isPaused || stopped) && (
              <div className="position-absolute bottom-0 end-0 mb-4 me-4 d-flex flex-column gap-3 align-items-center">
                {!stopped && (
                  <>
                    {!isPaused ? (
                      <Button
                        onClick={pauseRecording}
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: 'green',
                          border: 'none',
                        }}
                      >
                        ⏸
                      </Button>
                    ) : (
                      <Button
                        onClick={resumeRecording}
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: 'green',
                          border: 'none',
                        }}
                      >
                        ▶️
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        stopRecording();
                        setStopped(true);
                      }}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: 'gray',
                        border: 'none',
                      }}
                    >
                      ⏹
                    </Button>
                  </>
                )}

                {stopped && (
                  <Button
                    onClick={() => videoRef.current?.play()}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'green',
                      border: 'none',
                    }}
                  >
                    ▶️
                  </Button>
                )}
              </div>
            )}
            {/* After recording */}
            {!!videoBlob && (
              <div className="position-absolute bottom-0 start-50 translate-middle-x mb-4 d-flex gap-3">
                <Button variant="secondary" onClick={restartRecording}>
                  🔁 Restart
                </Button>
                <Button variant="success" onClick={saveStep2}>
                  Save and Next
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Description */}
        {step === 3 && (
          <div className="p-3">
            <h5>Update Description</h5>
            <Form.Control
              as="textarea"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step > 1 && (
          <Button variant="secondary" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button variant="success" onClick={saveStep1}>
            Save and Next
          </Button>
        )}
        {step === 3 && (
          <Button variant="success" onClick={saveStep3}>
            Finish
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MultiStepFlow;
